import { useMemo } from 'react';
import { getPageTitle } from '../config/hostname-page-titles';

/**
 * Custom hook to get the H1 title for a specific page based on current hostname
 * 
 * Returns the hostname-specific title if configured, otherwise returns null.
 * The component should handle fallback to domainConfig title.
 * 
 * @param {string} pageName - The page name (e.g., 'home', 'solutions', 'products')
 * @returns {string|null} - The H1 title for the page, or null if not configured
 * 
 * @example
 * const Home = () => {
 *   const h1Title = usePageTitle('home');
 *   // Fallback to domainConfig title
 *   return <h1>{h1Title || domainConfig.hero?.title || 'Default'}</h1>;
 * };
 */
export const usePageTitle = (pageName) => {
  return useMemo(() => {
    // Get hostname from window.location (client-side only)
    if (typeof window === 'undefined') {
      return null;
    }
    
    const hostname = window.location.hostname;
    const customTitle = getPageTitle(hostname, pageName);
    
    // Return null if no custom title, letting component use domainConfig fallback
    return customTitle || null;
  }, [pageName]);
};

/**
 * Get page title directly without hook (for use in non-component contexts)
 * 
 * @param {string} pageName - The page name
 * @returns {string|null} - The H1 title for the page, or null if not configured
 */
export const getPageTitleForCurrentHost = (pageName) => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  const hostname = window.location.hostname;
  const customTitle = getPageTitle(hostname, pageName);
  
  return customTitle || null;
};

export default usePageTitle;

